import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginLimiter } from '../../utils/rateLimiter';

const Login = ({ onToggleSignup, redirectUrl = '/dashboard', context }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  const { login, resetPassword, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture signup context and redirect from URL for Google OAuth signups
  const signupContext = searchParams.get('context') || context || null;
  const signupRedirect = searchParams.get('redirect') || null;

  // Capture referral code from URL or storage
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);

      // Store in localStorage with 90-day expiry
      localStorage.setItem('referralCode', JSON.stringify({
        ref,
        timestamp: Date.now(),
        expiresAt: Date.now() + (90 * 24 * 60 * 60 * 1000)
      }));

      // Also store in sessionStorage as fallback
      sessionStorage.setItem('referralCode', ref);
    } else {
      // Try to retrieve from storage
      const sessionRef = sessionStorage.getItem('referralCode');
      if (sessionRef) {
        setReferralCode(sessionRef);
      } else {
        const storedData = localStorage.getItem('referralCode');
        if (storedData) {
          try {
            const { ref: storedRef, expiresAt } = JSON.parse(storedData);
            if (Date.now() < expiresAt) {
              setReferralCode(storedRef);
            }
          } catch (e) {
            // Invalid stored data, ignore
          }
        }
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    // Check rate limiting
    const { allowed, timeUntilReset } = loginLimiter.canAttempt();
    if (!allowed) {
      const minutes = Math.ceil(timeUntilReset / 60000);
      setError(`Too many login attempts. Please wait before trying again.`);
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Record the attempt
      loginLimiter.recordAttempt();
      
      await login(email, password);
      
      // Redirect to the specified URL or dashboard
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      setError('Incorrect Username or Password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    try {
      setError('');
      setResetLoading(true);
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (error) {
      setError('Failed to send reset email: ' + error.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle(null, signupContext, signupRedirect);
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPortalName = () => {
    if (context === 'tournament') return 'Tournament Portal';
    if (context === 'creator') return 'Creator Portal';
    if (context === 'student') return 'Student Portal';
    return 'ChessWeb.site';
  };

  return (
    <div className="max-w-md mx-auto rounded-xl p-8" style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.1)' }}>
      <h2 className="text-2xl font-bold mb-8 text-center text-white">
        Sign in to {getPortalName()}
      </h2>

      {error && (
        <div className="px-4 py-3 rounded-lg mb-4 text-sm" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
          {error}
        </div>
      )}

      {/* Google Sign-In Button */}
      <button
        onClick={handleGoogleSignIn}
        disabled={loading}
        className="w-full flex items-center justify-center px-4 py-3 rounded-lg text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed mb-8 transition-all"
        style={{ background: 'white', color: '#333', border: 'none' }}
        onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
        onMouseLeave={e => e.currentTarget.style.background = 'white'}
      >
        <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        {loading ? 'Signing in...' : 'Continue with Google'}
      </button>

      {/* Return to Main Site */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => window.history.length > 1 ? window.history.back() : window.location.href = '/'}
          className="text-sm transition-colors inline-flex items-center bg-transparent border-none cursor-pointer p-0"
          style={{ color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        >
          <svg viewBox="0 0 448 512" fill="currentColor" className="w-3.5 h-3.5 mr-2" aria-hidden="true">
            <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
          </svg>
          Return to site
        </button>
      </div>

      {/* Terms & Privacy Links */}
      <div className="mt-3 text-center">
        <div className="flex justify-center gap-4 text-xs">
          <a
            href="/terms-and-conditions"
            className="transition-colors"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            Terms
          </a>
          <a
            href="/privacy-policy"
            className="transition-colors"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            Privacy
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login; 